/**
 * BSP Dungeon Generator
 * Recursively splits space into rooms, then connects them with corridors.
 * Enhanced with: zone theming, room blueprints, secret passages, progression gates.
 */

import type { GridMap, GeneratorConfig, Room, DungeonTheme, DungeonZone, RoomMetadata } from '../types';
import { createGrid, carveRoom, carveCorridor, addEntity, setTerrain, getCell } from '../grid';
import { SeededRandom } from '../random';
import {
  selectBlueprint,
  getEntranceBlueprint,
  applyBlueprint,
  getZoneLabel,
} from './dungeon-blueprints';

// ============================================================================
// BSP TREE
// ============================================================================

interface BSPNode {
  x: number;
  y: number;
  width: number;
  height: number;
  room?: Room;
  left?: BSPNode;
  right?: BSPNode;
}

const MIN_SPLIT_SIZE = 8;

// ============================================================================
// DUNGEON THEME NAMES
// ============================================================================

const THEME_NAMES: Record<DungeonTheme, string[]> = {
  fortress: ['Ironhold Keep', 'Bastion of Shadows', 'The Stone Garrison', 'Wardens Fortress'],
  crypt:    ['Tomb of the Forgotten', 'Ossuary of Whispers', 'The Pale Crypts', 'Catacombs of Dusk'],
  lair:     ['The Beast Den', 'Maw of the Deep', 'Feral Hollows', 'Predators Nest'],
  ruins:    ['Shattered Sanctum', 'The Crumbling Halls', 'Echoes of the Lost', 'Fallen Archive'],
  sewer:    ['The Murky Depths', 'Rat King Tunnels', 'Under-Canal Warrens', 'The Dripping Maze'],
};

// ============================================================================
// MAIN GENERATOR
// ============================================================================

export function generateBSPDungeon(config: GeneratorConfig): GridMap {
  const { width, height, seed } = config;
  const minRoomSize = config.minRoomSize ?? 4;
  const maxRoomSize = config.maxRoomSize ?? 10;
  const maxDepth = config.maxDepth ?? 5;
  const theme: DungeonTheme = config.dungeonTheme ?? 'fortress';
  const enableSecrets = config.enableSecrets ?? false;
  const enableKeys = config.enableKeys ?? false;
  const rng = new SeededRandom(seed);

  const map = createGrid(width, height, 'wall');
  map.seed = seed;
  map.name = rng.pick(THEME_NAMES[theme]);

  // Step 1: Build BSP tree (1-cell border)
  const root: BSPNode = { x: 1, y: 1, width: width - 2, height: height - 2 };
  splitNode(root, 0, maxDepth, rng);

  // Step 2: Place rooms in leaf nodes
  const rooms: Room[] = [];
  placeRooms(root, minRoomSize, maxRoomSize, rng, rooms);

  // Step 3: Carve rooms
  for (const room of rooms) {
    carveRoom(map, room.x, room.y, room.width, room.height);
  }

  // Step 4: Connect siblings + track connections
  const connections = new Map<number, Set<number>>();
  for (const room of rooms) connections.set(room.id, new Set());
  connectSiblings(map, root, rng, connections);

  // Step 5: Assign zones
  const zones = assignZones(root, rooms, theme);

  // Step 6: Apply room blueprints per zone
  const entranceRoom = rooms[0];
  const roomMetadata: RoomMetadata[] = [];

  for (const room of rooms) {
    const zone = zones.find(z => z.roomIds.includes(room.id));
    const zoneId = zone?.id ?? 0;
    const isEntrance = room.id === entranceRoom?.id;

    const blueprint = isEntrance
      ? getEntranceBlueprint()
      : selectBlueprint(theme, room.width * room.height, 0, rng);

    applyBlueprint(map, room, blueprint, rng, `r${room.id}`);

    roomMetadata.push({
      roomId: room.id,
      purpose: blueprint.purpose,
      zoneId,
      connections: [...(connections.get(room.id) ?? [])],
      x: room.x,
      y: room.y,
      width: room.width,
      height: room.height,
    });
  }

  // Step 7: Secret passages
  if (enableSecrets) {
    placeSecretPassages(map, rooms, connections, rng);
  }

  // Step 8: Progression gates (locked doors + keys)
  if (enableKeys && rooms.length >= 4) {
    placeProgressionGates(map, rooms, connections, rng);
  }

  // Step 9: Place player in entrance room
  if (entranceRoom) {
    const px = entranceRoom.x + Math.floor(entranceRoom.width / 2);
    const py = entranceRoom.y + Math.floor(entranceRoom.height / 2);
    // Ensure player tile is floor
    setTerrain(map, px, py, 'floor');
    return addEntity(
      { ...map, metadata: { dungeonZones: zones, rooms: roomMetadata } },
      { id: 'player', x: px, y: py, type: 'player', name: 'Player', lightRadius: 12 },
    );
  }

  map.metadata = { dungeonZones: zones, rooms: roomMetadata };
  return map;
}

// ============================================================================
// BSP SPLITTING
// ============================================================================

function splitNode(node: BSPNode, depth: number, maxDepth: number, rng: SeededRandom): void {
  if (depth >= maxDepth) return;
  if (node.width < MIN_SPLIT_SIZE * 2 && node.height < MIN_SPLIT_SIZE * 2) return;

  let splitHorizontal: boolean;
  if (node.width < MIN_SPLIT_SIZE * 2) {
    splitHorizontal = true;
  } else if (node.height < MIN_SPLIT_SIZE * 2) {
    splitHorizontal = false;
  } else {
    splitHorizontal = node.height >= node.width ? true : rng.bool(0.4);
  }

  if (splitHorizontal) {
    const minSplit = Math.floor(node.height * 0.3);
    const maxSplit = Math.floor(node.height * 0.7);
    if (minSplit < MIN_SPLIT_SIZE || maxSplit < MIN_SPLIT_SIZE) return;

    const split = rng.int(
      Math.max(minSplit, MIN_SPLIT_SIZE),
      Math.min(maxSplit, node.height - MIN_SPLIT_SIZE),
    );

    node.left = { x: node.x, y: node.y, width: node.width, height: split };
    node.right = { x: node.x, y: node.y + split, width: node.width, height: node.height - split };
  } else {
    const minSplit = Math.floor(node.width * 0.3);
    const maxSplit = Math.floor(node.width * 0.7);
    if (minSplit < MIN_SPLIT_SIZE || maxSplit < MIN_SPLIT_SIZE) return;

    const split = rng.int(
      Math.max(minSplit, MIN_SPLIT_SIZE),
      Math.min(maxSplit, node.width - MIN_SPLIT_SIZE),
    );

    node.left = { x: node.x, y: node.y, width: split, height: node.height };
    node.right = { x: node.x + split, y: node.y, width: node.width - split, height: node.height };
  }

  splitNode(node.left, depth + 1, maxDepth, rng);
  splitNode(node.right, depth + 1, maxDepth, rng);
}

// ============================================================================
// ROOM PLACEMENT
// ============================================================================

function placeRooms(
  node: BSPNode, minSize: number, maxSize: number, rng: SeededRandom, rooms: Room[],
): void {
  if (!node.left && !node.right) {
    const rw = rng.int(minSize, Math.min(maxSize, node.width - 2));
    const rh = rng.int(minSize, Math.min(maxSize, node.height - 2));
    const rx = rng.int(node.x + 1, node.x + node.width - rw - 1);
    const ry = rng.int(node.y + 1, node.y + node.height - rh - 1);

    const room: Room = { x: rx, y: ry, width: rw, height: rh, id: rooms.length };
    node.room = room;
    rooms.push(room);
    return;
  }

  if (node.left) placeRooms(node.left, minSize, maxSize, rng, rooms);
  if (node.right) placeRooms(node.right, minSize, maxSize, rng, rooms);
}

// ============================================================================
// SIBLING CONNECTION
// ============================================================================

function findRoom(node: BSPNode): Room | null {
  if (node.room) return node.room;
  if (node.left) {
    const r = findRoom(node.left);
    if (r) return r;
  }
  if (node.right) {
    const r = findRoom(node.right);
    if (r) return r;
  }
  return null;
}

function connectSiblings(
  map: GridMap, node: BSPNode, rng: SeededRandom,
  connections: Map<number, Set<number>>,
): void {
  if (!node.left || !node.right) return;

  connectSiblings(map, node.left, rng, connections);
  connectSiblings(map, node.right, rng, connections);

  const roomA = findRoom(node.left);
  const roomB = findRoom(node.right);

  if (roomA && roomB) {
    const ax = roomA.x + Math.floor(roomA.width / 2);
    const ay = roomA.y + Math.floor(roomA.height / 2);
    const bx = roomB.x + Math.floor(roomB.width / 2);
    const by = roomB.y + Math.floor(roomB.height / 2);
    carveCorridor(map, ax, ay, bx, by, rng.bool());

    connections.get(roomA.id)?.add(roomB.id);
    connections.get(roomB.id)?.add(roomA.id);
  }
}

// ============================================================================
// ZONE ASSIGNMENT
// ============================================================================

function assignZones(root: BSPNode, rooms: Room[], theme: DungeonTheme): DungeonZone[] {
  if (rooms.length === 0) return [];

  // Use top-level BSP split to divide rooms into groups
  const groups: Room[][] = [];
  collectRoomGroups(root, groups, 0, 2); // split up to 2 levels deep → up to 3 groups

  // If we only got 1 group, split it in half
  if (groups.length <= 1 && rooms.length >= 4) {
    const mid = Math.floor(rooms.length / 2);
    groups.length = 0;
    groups.push(rooms.slice(0, mid));
    groups.push(rooms.slice(mid));
  }

  const zones: DungeonZone[] = groups.map((group, i) => {
    const xs = group.map(r => r.x);
    const ys = group.map(r => r.y);
    const x2s = group.map(r => r.x + r.width);
    const y2s = group.map(r => r.y + r.height);

    return {
      id: i,
      theme: getZoneLabel(theme, i),
      roomIds: group.map(r => r.id),
      bounds: {
        x: Math.min(...xs),
        y: Math.min(...ys),
        width: Math.max(...x2s) - Math.min(...xs),
        height: Math.max(...y2s) - Math.min(...ys),
      },
    };
  });

  return zones;
}

function collectRoomGroups(node: BSPNode, groups: Room[][], depth: number, maxDepth: number): void {
  if (depth >= maxDepth || (!node.left && !node.right)) {
    const rooms = collectAllRooms(node);
    if (rooms.length > 0) groups.push(rooms);
    return;
  }

  if (node.left) collectRoomGroups(node.left, groups, depth + 1, maxDepth);
  if (node.right) collectRoomGroups(node.right, groups, depth + 1, maxDepth);
}

function collectAllRooms(node: BSPNode): Room[] {
  const rooms: Room[] = [];
  if (node.room) rooms.push(node.room);
  if (node.left) rooms.push(...collectAllRooms(node.left));
  if (node.right) rooms.push(...collectAllRooms(node.right));
  return rooms;
}

// ============================================================================
// SECRET PASSAGES
// ============================================================================

function placeSecretPassages(
  map: GridMap, rooms: Room[],
  connections: Map<number, Set<number>>, rng: SeededRandom,
): void {
  let secretCount = 0;
  const maxSecrets = rng.int(2, 4);

  // Find unconnected room pairs that are close enough
  const candidates: Array<{ a: Room; b: Room; dist: number; wallTileA: { x: number; y: number }; wallTileB: { x: number; y: number } }> = [];

  for (let i = 0; i < rooms.length; i++) {
    for (let j = i + 1; j < rooms.length; j++) {
      const a = rooms[i];
      const b = rooms[j];
      if (connections.get(a.id)?.has(b.id)) continue; // already connected

      // Find closest wall tiles between rooms
      const closest = findClosestWallTiles(a, b);
      if (closest && closest.dist <= 3) {
        candidates.push({ a, b, ...closest });
      }
    }
  }

  // Sort by distance (closest first) and place secrets
  candidates.sort((a, b) => a.dist - b.dist);

  for (const cand of candidates) {
    if (secretCount >= maxSecrets) break;

    // Carve 1-wide passage between the two wall tiles
    const { wallTileA, wallTileB } = cand;
    carveThinPassage(map, wallTileA.x, wallTileA.y, wallTileB.x, wallTileB.y);

    // Place secret door at the entrance to room A's side
    setTerrain(map, wallTileA.x, wallTileA.y, 'door');
    // Add entity metadata for the secret door
    map.entities.push({
      id: `secret_door_${secretCount}`,
      x: wallTileA.x, y: wallTileA.y,
      type: 'object', name: 'Secret Door',
      metadata: { secret: true },
    });

    secretCount++;
  }
}

function findClosestWallTiles(a: Room, b: Room): { dist: number; wallTileA: { x: number; y: number }; wallTileB: { x: number; y: number } } | null {
  let bestDist = Infinity;
  let bestA = { x: 0, y: 0 };
  let bestB = { x: 0, y: 0 };

  // Check edges of room A against edges of room B
  const edgesA = getRoomEdges(a);
  const edgesB = getRoomEdges(b);

  for (const ea of edgesA) {
    for (const eb of edgesB) {
      const d = Math.abs(ea.x - eb.x) + Math.abs(ea.y - eb.y);
      if (d < bestDist && d > 0) {
        bestDist = d;
        bestA = ea;
        bestB = eb;
      }
    }
  }

  return bestDist <= 5 ? { dist: bestDist, wallTileA: bestA, wallTileB: bestB } : null;
}

function getRoomEdges(room: Room): Array<{ x: number; y: number }> {
  const edges: Array<{ x: number; y: number }> = [];
  // Top and bottom walls (one tile outside the room)
  for (let x = room.x; x < room.x + room.width; x++) {
    edges.push({ x, y: room.y - 1 });
    edges.push({ x, y: room.y + room.height });
  }
  // Left and right walls
  for (let y = room.y; y < room.y + room.height; y++) {
    edges.push({ x: room.x - 1, y });
    edges.push({ x: room.x + room.width, y });
  }
  return edges;
}

function carveThinPassage(map: GridMap, x1: number, y1: number, x2: number, y2: number): void {
  // Simple L-shaped 1-wide corridor
  const midX = x2;
  for (let x = Math.min(x1, midX); x <= Math.max(x1, midX); x++) {
    setTerrain(map, x, y1, 'floor');
  }
  for (let y = Math.min(y1, y2); y <= Math.max(y1, y2); y++) {
    setTerrain(map, midX, y, 'floor');
  }
}

// ============================================================================
// PROGRESSION GATES (locked doors + keys)
// ============================================================================

function placeProgressionGates(
  map: GridMap, rooms: Room[],
  connections: Map<number, Set<number>>, rng: SeededRandom,
): void {
  if (rooms.length < 4) return;

  // BFS from entrance room to find longest path
  const entranceId = rooms[0].id;
  const distances = new Map<number, number>();
  const parents = new Map<number, number>();
  const queue: number[] = [entranceId];
  distances.set(entranceId, 0);

  while (queue.length > 0) {
    const current = queue.shift()!;
    const dist = distances.get(current)!;
    for (const neighbor of connections.get(current) ?? []) {
      if (!distances.has(neighbor)) {
        distances.set(neighbor, dist + 1);
        parents.set(neighbor, current);
        queue.push(neighbor);
      }
    }
  }

  // Find the farthest room
  let farthestId = entranceId;
  let maxDist = 0;
  for (const [id, dist] of distances) {
    if (dist > maxDist) {
      maxDist = dist;
      farthestId = id;
    }
  }

  if (maxDist < 3) return; // path too short for meaningful gates

  // Trace the critical path
  const criticalPath: number[] = [];
  let current = farthestId;
  while (current !== undefined && current !== entranceId) {
    criticalPath.unshift(current);
    current = parents.get(current)!;
  }
  criticalPath.unshift(entranceId);

  // Place 1-2 locked doors on the critical path
  const gateCount = Math.min(rng.int(1, 2), criticalPath.length - 2);
  const gatePositions: number[] = [];

  // Place gates at roughly even intervals along the path
  for (let i = 0; i < gateCount; i++) {
    const idx = Math.floor((criticalPath.length * (i + 1)) / (gateCount + 1));
    if (idx > 0 && idx < criticalPath.length) {
      gatePositions.push(idx);
    }
  }

  for (let gi = 0; gi < gatePositions.length; gi++) {
    const gateIdx = gatePositions[gi];
    const beforeRoom = rooms.find(r => r.id === criticalPath[gateIdx - 1]);
    const afterRoom = rooms.find(r => r.id === criticalPath[gateIdx]);
    if (!beforeRoom || !afterRoom) continue;

    const keyId = `key_${gi}`;

    // Find a corridor tile between the two rooms to place the locked door
    const doorPos = findCorridorTileBetween(map, beforeRoom, afterRoom);
    if (doorPos) {
      setTerrain(map, doorPos.x, doorPos.y, 'door');
      map.entities.push({
        id: `locked_door_${gi}`,
        x: doorPos.x, y: doorPos.y,
        type: 'object', name: 'Locked Door',
        metadata: { locked: true, keyId },
      });
    }

    // Place the key in a room that is reachable before the gate
    // Pick a random room from the rooms before this gate on the critical path
    const accessibleRoomIds = criticalPath.slice(0, gateIdx);
    const keyRoomId = rng.pick(accessibleRoomIds);
    const keyRoom = rooms.find(r => r.id === keyRoomId);
    if (keyRoom) {
      const kx = keyRoom.x + rng.int(0, keyRoom.width - 1);
      const ky = keyRoom.y + rng.int(0, keyRoom.height - 1);
      const cell = getCell(map, kx, ky);
      if (cell && cell.terrain === 'floor') {
        map.entities.push({
          id: keyId,
          x: kx, y: ky,
          type: 'object', name: 'Key',
          metadata: { keyId },
        });
      }
    }
  }
}

function findCorridorTileBetween(
  map: GridMap, roomA: Room, roomB: Room,
): { x: number; y: number } | null {
  // Walk from center of A toward center of B, find the first floor tile
  // that's outside both rooms (i.e., in the corridor)
  const ax = roomA.x + Math.floor(roomA.width / 2);
  const ay = roomA.y + Math.floor(roomA.height / 2);
  const bx = roomB.x + Math.floor(roomB.width / 2);
  const by = roomB.y + Math.floor(roomB.height / 2);

  // Step along the line from A to B
  const dx = Math.sign(bx - ax);
  const dy = Math.sign(by - ay);

  // Try horizontal first, then vertical
  let x = ax;
  let y = ay;
  for (let steps = 0; steps < 50; steps++) {
    if (x !== bx) x += dx;
    else if (y !== by) y += dy;
    else break;

    const cell = getCell(map, x, y);
    if (!cell || cell.terrain !== 'floor') continue;

    // Check if outside both rooms
    const inA = x >= roomA.x && x < roomA.x + roomA.width &&
                y >= roomA.y && y < roomA.y + roomA.height;
    const inB = x >= roomB.x && x < roomB.x + roomB.width &&
                y >= roomB.y && y < roomB.y + roomB.height;

    if (!inA && !inB) {
      return { x, y };
    }
  }

  return null;
}
